import { Injectable, inject, effect } from '@angular/core';
import { AuthService } from './auth.service';

@Injectable({
  providedIn: 'root'
})
export class IdleService {
  private authService = inject(AuthService);
  private timeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly IDLE_TIMEOUT = 30 * 60 * 1000; // 30 minutes in milliseconds
  private warningTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before logout

  constructor() {
    // Start/stop idle tracking based on authentication status
    effect(() => {
      if (this.authService.isAuthenticated()) {
        this.startIdleTimer();
      } else {
        this.stopIdleTimer();
      }
    });

    // Listen for user activity events
    this.setupActivityListeners();
  }

  private setupActivityListeners(): void {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    events.forEach(event => {
      document.addEventListener(event, () => this.onUserActivity(), true);
    });
  }

  private onUserActivity(): void {
    // Notify AuthService that user is active
    this.authService.setUserActivity(true);

    // Reset idle timer
    this.resetIdleTimer();
  }

  private startIdleTimer(): void {
    this.resetIdleTimer();
  }

  private resetIdleTimer(): void {
    this.clearIdleTimer();

    // Set idle timeout
    this.timeoutId = setTimeout(() => {
      // Notify AuthService that user is idle
      this.authService.setUserActivity(false);

      // Log out user
      this.authService.logout();
    }, this.IDLE_TIMEOUT);

    // Optional: Set warning timeout (5 minutes before logout)
    this.warningTimeoutId = setTimeout(() => {
      this.showIdleWarning();
    }, this.IDLE_TIMEOUT - this.WARNING_TIME);
  }

  private showIdleWarning(): void {
    // Optional: Show a warning modal or notification
    console.warn('You will be logged out due to inactivity in 5 minutes');
    // You can emit an event or show a modal here
  }

  private clearIdleTimer(): void {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.warningTimeoutId) {
      clearTimeout(this.warningTimeoutId);
      this.warningTimeoutId = null;
    }
  }

  private stopIdleTimer(): void {
    this.clearIdleTimer();
    // Reset activity status when stopping
    this.authService.setUserActivity(false);
  }

  /**
   * Manually extend the session (useful when user responds to idle warning)
   */
  extendSession(): void {
    this.onUserActivity();
  }

  /**
   * Get remaining time before idle logout (in milliseconds)
   */
  getRemainingTime(): number {
    // This would require tracking the start time - simplified for now
    return this.IDLE_TIMEOUT;
  }
}